import { useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { PageCard } from './PageCard';
import { PostCard } from './PostCard';
import type { Post, KontentinoPage, PostAssignments } from '../types';

interface ReviewSummaryProps {
  posts: Post[];
  selectedPages: KontentinoPage[];
  postAssignments: PostAssignments;
  onBack: () => void;
  onConfirm: () => void;
}

export function ReviewSummary({
  posts,
  selectedPages,
  postAssignments,
  onBack,
  onConfirm,
}: ReviewSummaryProps) {
  // Group posts by page
  const groupedAssignments = useMemo(() => {
    return selectedPages
      .map((page) => {
        const assignedPostIds = postAssignments[page.id] || [];
        const assignedPosts = posts.filter((post) => assignedPostIds.includes(post.id));
        return {
          page,
          posts: assignedPosts,
        };
      })
      .filter((group) => group.posts.length > 0);
  }, [selectedPages, postAssignments, posts]);

  const totalPosts = useMemo(() => {
    return Object.values(postAssignments).reduce((sum, posts) => sum + posts.length, 0);
  }, [postAssignments]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Review & Confirm</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Review your selections before scheduling
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-2 text-primary font-medium">
          <CheckCircle2 className="h-5 w-5" />
          <span>
            {totalPosts} {totalPosts === 1 ? 'post' : 'posts'} assigned to{' '}
            {groupedAssignments.length}{' '}
            {groupedAssignments.length === 1 ? 'page' : 'pages'}
          </span>
        </div>
      </div>

      {/* Grouped assignments */}
      <div className="space-y-4">
        {groupedAssignments.map(({ page, posts }) => (
          <div key={page.id} className="border rounded-lg p-4 space-y-3 bg-card">
            {/* Page header */}
            <div className="flex items-center justify-between pb-2 border-b">
              <PageCard page={page} />
              <span className="text-sm text-muted-foreground">
                {posts.length} {posts.length === 1 ? 'post' : 'posts'}
              </span>
            </div>

            {/* Post list */}
            <div className="space-y-2 pl-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-start gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
                >
                  <span className="text-muted-foreground">•</span>
                  <div className="flex-1 min-w-0">
                    <PostCard post={post} compact />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {groupedAssignments.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No posts assigned. Please go back and assign posts to pages.
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          ← Back
        </Button>
        <Button onClick={onConfirm} disabled={totalPosts === 0}>
          Schedule {totalPosts} {totalPosts === 1 ? 'Post' : 'Posts'}
        </Button>
      </div>
    </div>
  );
}
